import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import { redirectToStore } from "src/utils/getAppVersion";
import { isNativeIapSupported } from "src/services/iap";
import { getProdFrontendFallbackUrl } from "src/config/frontendUrls";

interface BenchUpgradeSubscriptionFreeProps {
    setIsPaymentOpen?: (open: boolean) => void;
    setInitialPaymentView?: (view: string) => void;
}

const BenchUpgradeSubscriptionFree: React.FC<BenchUpgradeSubscriptionFreeProps> = ({ setIsPaymentOpen, setInitialPaymentView }) => {
    // Check if running on native platform (iOS/Android)
    const isNativePlatform = Capacitor.isNativePlatform();
    const isHybridPlatform = isPlatform("hybrid");
    const isRunningNatively = isNativePlatform || isHybridPlatform;

    // Handler for Download App button - redirects to App Store or Google Play
    const handleDownloadApp = async (event: React.MouseEvent) => {
        event.stopPropagation();
        await redirectToStore();
    };


    // Function to open web app in Capacitor Browser for native platforms
    const openWebAppInBrowser = async (path: string = "/home", queryParams: string = "") => {
        if (!isRunningNatively) {
            // On web, use normal navigation
            return false;
        }

        try {
            // Get base URL from environment variable
            const baseUrl = process.env.REACT_APP_FRONTEND_URI_WEB || getProdFrontendFallbackUrl();
            // Remove trailing slash and /home if present
            const cleanBaseUrl = baseUrl.replace(/\/$/, "").replace(/\/home$/, "");
            const fullUrl = `${cleanBaseUrl}${path}${queryParams ? `?${queryParams}` : ""}`;

            console.log("📱 UpgradeSubscriptionFree: Opening web app in Capacitor Browser:", fullUrl);

            // Open in Capacitor Browser
            await Browser.open({ url: fullUrl, presentationStyle: "popover" });
            return true;
        } catch (error) {
            console.error("UpgradeSubscriptionFree: Error opening Capacitor Browser:", error);
            return false;
        }
    };

    const handleUpgradeClick = async () => {
        // Native iOS/Android: open Manage Membership in-app (same as PaymentMethodMenu — no Capacitor Browser).
        if (isNativeIapSupported()) {
            if (setInitialPaymentView && setIsPaymentOpen) {
                setInitialPaymentView("manageMembership");
                setIsPaymentOpen(true);
                console.log("✅ BenchUpgradeSubscriptionFree: Opened manageMembership drawer (native iOS/Android)");
            }
            return;
        }

        // Other native contexts (without in-app billing support): fallback to Capacitor Browser web flow.
        if (isRunningNatively) {
            // Pass query parameter to open payment drawer directly with manageMembership view
            const opened = await openWebAppInBrowser("/home", "openPayment=manageMembership&fromApp=true");
            if (opened) {
                console.log("✅ BenchUpgradeSubscriptionFree: Opened Capacitor Browser with manageMembership drawer");
                return;
            }
        }

        // On web, open payment drawer directly
        if (setInitialPaymentView && setIsPaymentOpen) {
            setInitialPaymentView("manageMembership");
            setIsPaymentOpen(true);
            console.log("✅ BenchUpgradeSubscriptionFree: Opened manageMembership drawer (web)");
        }
    };
    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, width: "100%", px: 2, pt: 0, }}>

            {/* First Button - Download App and Sign In Free (Light Gray)  (only show it if we are not running natively)*/}
            {!isRunningNatively && (
                <Button
                    variant="contained"
                    onClick={handleDownloadApp}
                    sx={{
                        width: "100%",
                        maxWidth: "400px",
                        bgcolor: `${PureLightThemeCaaS.colors.alpha.gray[30]}`,
                        color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                        borderRadius: 2,
                        p: 0.5,
                        textTransform: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0,
                        "&:hover": {
                            bgcolor: `${PureLightThemeCaaS.colors.alpha.gray[30]}`,
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                        }}
                    >
                        Download App 
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: "normal",
                            color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                            textAlign: "center",
                        }}
                    >
                        Get even more credits
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: "normal",
                            color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                            textAlign: "center",
                        }}
                    >
                        Save your leagues
                    </Typography>
                </Button>
            )}

            {/* Second Button - Go Premium (Orange) */}
            <Button
                variant="contained"
                onClick={handleUpgradeClick}
                sx={{
                    width: "100%",
                    maxWidth: "400px",
                    bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    borderRadius: 2,
                    p: 0.5,

                    textTransform: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0,
                    "&:hover": {
                        bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    }}
                >
                    Go Premium
                </Typography>
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: "normal",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        textAlign: "center",
                    }}
                >
                    24/7 priority access.
                </Typography>
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: "normal",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        textAlign: "center",
                    }}
                >
                    No more benching.
                </Typography>
            </Button>


        </Box>
    );
};

export default BenchUpgradeSubscriptionFree;
